/**
 * RetroactiveContractModal — Onboarding rétroactif CDI/CDD.
 *
 * Brief CEO (mémo audio 2026-08-03) :
 *   « Un ancien collaborateur en CDI a déjà signé son contrat en début
 *     d'année. Je veux qu'il soit sur PREREC (matricule, statut hired,
 *     exposé Booking/Reporting/PayFit) mais je vais juste uploader le PDF
 *     signé qu'on a déjà — pas de génération depuis le template. »
 *
 * Ce composant est le pendant "sans template" du couple `prepare` +
 * `MarkOfflineSignedModal`. Un seul écran, une seule requête multipart
 * POST /api/contracts/candidates/{cid}/onboard-retroactive.
 */
import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2, FileText, Zap } from "lucide-react";

import { api } from "../../lib/api";


const STATUT_OPTIONS = [
    { value: "cdi", label: "CDI — Durée indéterminée" },
    { value: "cdd", label: "CDD — Durée déterminée" },
    { value: "alternant", label: "Alternant" },
    { value: "assistant", label: "Assistant" },
];

const ROLE_OPTIONS = [
    { value: "recruteur_donateurs", label: "Recruteur de donateurs (RD)" },
    { value: "responsable_equipe", label: "Responsable d'équipe (RE)" },
    { value: "charge_developpement", label: "Chargé de développement (CD)" },
];

const REGION_OPTIONS = [
    { value: "idf", label: "Île-de-France" },
    { value: "nouvelle_aquitaine", label: "Nouvelle-Aquitaine" },
    { value: "occitanie", label: "Occitanie" },
    { value: "paca", label: "PACA" },
];


export default function RetroactiveContractModal({ candidate, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [statutEmploi, setStatutEmploi] = useState("cdi");
    const [role, setRole] = useState("recruteur_donateurs");
    const [region, setRegion] = useState("idf");
    const [signedAt, setSignedAt] = useState(() => new Date().toISOString().slice(0, 10));
    const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const fileInputRef = useRef(null);

    function pickFile(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.type && f.type !== "application/pdf") {
            toast.error("Format non supporté", { description: "Merci de fournir un PDF (application/pdf)" });
            return;
        }
        if (f.size > 15 * 1024 * 1024) {
            toast.error("PDF trop lourd", { description: `${Math.round(f.size / 1024)} Ko > 15 Mo max` });
            return;
        }
        setFile(f);
    }

    async function submit(e) {
        e.preventDefault();
        if (!file) {
            toast.error("Sélectionnez le PDF signé");
            return;
        }
        try {
            setBusy(true);
            const fd = new FormData();
            fd.append("pdf", file);
            fd.append("signed_at", signedAt);
            fd.append("statut_emploi", statutEmploi);
            fd.append("role", role);
            fd.append("region", region);
            if (startDate) fd.append("start_date", startDate);
            if (note.trim()) fd.append("note", note.trim());

            const res = await api.post(
                `/contracts/candidates/${candidate.id}/onboard-retroactive`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success("Collaborateur onboardé ✨", {
                description: `Contrat ${statutEmploi.toUpperCase()} enregistré (signé le ${signedAt}). Un matricule est attribué automatiquement, statut → embauché.`,
                duration: 9000,
            });
            onDone?.(res.data);
            onClose?.();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Erreur lors de l'onboarding rétroactif";
            toast.error(msg, { duration: 8000 });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="retro-contract-modal-backdrop"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-shariti-gray max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="retro-contract-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-shariti-gray shrink-0">
                    <h2 className="text-lg font-black text-shariti-black inline-flex items-center gap-2">
                        <Zap className="h-4 w-4 text-shariti-orange" />
                        Onboarder rétroactivement — contrat déjà signé
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="retro-contract-close"
                        className="text-slate-400 hover:text-shariti-black"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Explainer banner */}
                <div className="mx-6 mt-4 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 shrink-0" data-testid="retro-contract-explainer">
                    <div className="font-bold mb-0.5">Aucune génération depuis le template</div>
                    <div className="text-emerald-800">
                        Ce flux est pour les collaborateurs dont le contrat a déjà été signé
                        (typiquement un ancien CDI). Le PDF fourni est stocké tel quel, le
                        collaborateur passe directement en <strong>embauché</strong>, un matricule
                        est attribué et il devient visible dans Booking / Reporting / PayFit.
                    </div>
                </div>

                <form onSubmit={submit} className="flex-1 overflow-y-auto" data-testid="retro-contract-form">
                    <div className="p-6 space-y-4">

                        {/* Statut d'emploi */}
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Type d&apos;emploi</span>
                                <select
                                    value={statutEmploi}
                                    onChange={(e) => setStatutEmploi(e.target.value)}
                                    data-testid="retro-contract-statut"
                                    className="mt-1 w-full border border-slate-200 rounded-md bg-white px-3 py-2 text-sm"
                                >
                                    {STATUT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Poste</span>
                                <select
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    data-testid="retro-contract-role"
                                    className="mt-1 w-full border border-slate-200 rounded-md bg-white px-3 py-2 text-sm"
                                >
                                    {ROLE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Région</span>
                                <select
                                    value={region}
                                    onChange={(e) => setRegion(e.target.value)}
                                    data-testid="retro-contract-region"
                                    className="mt-1 w-full border border-slate-200 rounded-md bg-white px-3 py-2 text-sm"
                                >
                                    {REGION_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Date de signature</span>
                                <input
                                    type="date"
                                    value={signedAt}
                                    onChange={(e) => setSignedAt(e.target.value)}
                                    data-testid="retro-contract-signed-at"
                                    className="mt-1 w-full border border-slate-200 rounded-md bg-white px-3 py-2 text-sm"
                                />
                            </label>
                        </div>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                Date de début (utilisée par Booking / PayFit)
                            </span>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                data-testid="retro-contract-start-date"
                                className="mt-1 w-full border border-slate-200 rounded-md bg-white px-3 py-2 text-sm"
                            />
                            {statutEmploi === "cdi" && (
                                <p className="mt-1 text-[10px] text-slate-500">
                                    CDI : la date de fin est fixée automatiquement à +30 ans (durée indéterminée).
                                </p>
                            )}
                        </label>

                        {/* PDF upload */}
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-shariti-orange transition" data-testid="retro-contract-dropzone">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="application/pdf"
                                onChange={pickFile}
                                data-testid="retro-contract-file-input"
                                className="hidden"
                            />
                            {file ? (
                                <div className="flex items-center gap-3">
                                    <FileText className="h-6 w-6 text-shariti-orange" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-shariti-black truncate">{file.name}</div>
                                        <div className="text-[11px] text-slate-500">
                                            {Math.round(file.size / 1024)} Ko · {file.type || "application/pdf"}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFile(null)}
                                        data-testid="retro-contract-file-clear"
                                        className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-red-600"
                                    >
                                        Retirer
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="retro-contract-file-pick"
                                    className="w-full flex flex-col items-center gap-2 py-3 text-slate-500 hover:text-shariti-orange"
                                >
                                    <Upload className="h-6 w-6" />
                                    <span className="text-sm font-bold">Sélectionner le contrat déjà signé (PDF)</span>
                                    <span className="text-[11px] text-slate-400">Max 15 Mo · application/pdf</span>
                                </button>
                            )}
                        </div>

                        {/* Note */}
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                Note interne (optionnel — traçabilité URSSAF)
                            </span>
                            <textarea
                                rows={2}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                data-testid="retro-contract-note"
                                placeholder="Ex : CDI signé le 15/01/2026 en présentiel, contrat papier archivé PDF chez la RH."
                                className="mt-1 w-full border border-slate-200 rounded-md bg-white px-3 py-2 text-sm resize-y"
                            />
                        </label>
                    </div>
                </form>

                {/* Footer */}
                <div className="border-t border-shariti-gray shrink-0 px-6 py-4 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        data-testid="retro-contract-cancel"
                        className="px-4 py-2 rounded-md text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !file}
                        data-testid="retro-contract-submit"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-black bg-shariti-orange text-white hover:bg-shariti-black disabled:opacity-40"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                        Onboarder & attribuer matricule
                    </button>
                </div>
            </div>
        </div>
    );
}
